import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import dashboardManagerService from '../services/dashboardManagerService';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { getImageUrl } from '../services/imageService';
import Loading from '../components/common/Loading';
import './DashboardManager.css';

const DashboardManager = () => {
    const [activeTab, setActiveTab] = useState('products');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Dashboard Content
    const [dashboardContent, setDashboardContent] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    // One entry per hero slide: the product plus the copy that overrides what
    // the product itself says. Order here is the order of the slides.
    const [heroSlides, setHeroSlides] = useState([]);

    // Section heading + subheading for the category showcase
    const [sectionText, setSectionText] = useState({ heading: '', subheading: '' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');

            const [contentData, productsData, categoriesData, sectionData] = await Promise.all([
                dashboardManagerService.getDashboardContent(),
                productService.getAllProducts({ limit: 100 }),
                categoryService.getAllCategories(),
                dashboardManagerService.getHomeSection('category_showcase').catch(() => null),
            ]);

            setDashboardContent(contentData.content);
            setAllProducts(productsData.products || []);
            setAllCategories(categoriesData.categories || []);
            // Set selected items from dashboard content
            const featuredProducts = contentData.content
                .filter((item) => item.content_type === 'featured_product')
                .map((item) => item.product_id);
            const featuredCategoryRows = contentData.content
                .filter((item) => item.content_type === 'featured_category');

            setSelectedProducts(featuredProducts);
            setSelectedCategories(featuredCategoryRows.map((r) => r.category_id));

            const heroRows = contentData.content
                .filter((item) => item.content_type === 'hero_product')
                .sort((a, b) => a.display_order - b.display_order)
                .map((row) => ({
                    product_id: row.product_id,
                    kicker: row.kicker || '',
                    title: row.title || '',
                    description: row.description || '',
                    button_text: row.button_text || '',
                }));
            setHeroSlides(heroRows);

            const section = sectionData?.section;
            setSectionText({
                heading: section?.heading || '',
                subheading: section?.subheading || '',
            });
        } catch (err) {
            setError(err.message || 'Failed to load dashboard data');
            console.error('Error loading dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleProductToggle = (productId) => {
        setSelectedProducts((prev) =>
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId]
        );
    };

    const MAX_FEATURED_CATEGORIES = 4;
    const MAX_HERO_PRODUCTS = 3;

    const handleHeroToggle = (productId) => {
        setHeroSlides((prev) => {
            if (prev.some((slide) => slide.product_id === productId)) {
                setError('');
                return prev.filter((slide) => slide.product_id !== productId);
            }
            if (prev.length >= MAX_HERO_PRODUCTS) {
                setError(`The hero holds up to ${MAX_HERO_PRODUCTS} products. Deselect one to add another.`);
                return prev;
            }
            setError('');
            return [
                ...prev,
                {
                    product_id: productId,
                    kicker: '',
                    title: '',
                    description: '',
                    button_text: '',
                },
            ];
        });
    };

    const handleHeroCopyChange = (productId, field, value) => {
        setHeroSlides((prev) =>
            prev.map((slide) =>
                slide.product_id === productId ? { ...slide, [field]: value } : slide
            )
        );
    };

    const handleCategoryToggle = (categoryId) => {
        setSelectedCategories((prev) => {
            if (prev.includes(categoryId)) {
                setError('');
                return prev.filter((id) => id !== categoryId);
            }
            if (prev.length >= MAX_FEATURED_CATEGORIES) {
                setError(`You can feature up to ${MAX_FEATURED_CATEGORIES} categories. Deselect one to add another.`);
                return prev;
            }
            setError('');
            return [...prev, categoryId];
        });
    };

    // Each tab saves only its own slice of the dashboard content, so saving
    // one never clears another.
    const saveContent = async (payload, message, extra = []) => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            await Promise.all([
                dashboardManagerService.updateDashboardContent(payload),
                ...extra,
            ]);

            setSuccess(message);
            await loadData();
        } catch (err) {
            setError(err.message || 'Failed to update dashboard content');
        } finally {
            setLoading(false);
        }
    };

    const saveFeaturedProducts = () =>
        saveContent(
            { featured_products: selectedProducts },
            'Featured products updated successfully!'
        );

    const saveFeaturedCategories = () =>
        saveContent(
            { featured_categories: selectedCategories },
            'Featured categories updated successfully!',
            [
                dashboardManagerService.upsertHomeSection('category_showcase', {
                    heading: sectionText.heading,
                    subheading: sectionText.subheading,
                    is_active: true,
                }),
            ]
        );

    const saveHeroProducts = () =>
        saveContent({ hero_products: heroSlides }, 'Hero products updated successfully!');

    if (loading && !dashboardContent) {
        return <Loading message="Loading dashboard manager..." />;
    }

    return (
        <div className="dashboard-manager-container">
            <h1 className="page-title">Dashboard Manager</h1>
            <p className="page-subtitle">Manage homepage featured content and advertisements</p>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    Featured Products
                </button>
                <button
                    className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
                    onClick={() => setActiveTab('categories')}
                >
                    Featured Categories
                </button>
                <button
                    className={`tab ${activeTab === 'hero' ? 'active' : ''}`}
                    onClick={() => setActiveTab('hero')}
                >
                    Hero Products
                </button>
            </div>

            <p className="dashboard-manager-note">
                Hero banners and promotional advertisements now live under{' '}
                <Link to="/campaigns">Campaigns</Link>, where they can be
                scheduled and linked to a curated product list.
            </p>

            {activeTab === 'products' && (
                <div className="tab-content">
                    <h2>Select Featured Products</h2>
                    <p>Choose up to 8 products to feature on the homepage</p>
                    <div className="selection-grid">
                        {allProducts.map((product) => (
                            <div
                                key={product.id}
                                className={`selection-card ${selectedProducts.includes(product.id) ? 'selected' : ''}`}
                                onClick={() => handleProductToggle(product.id)}
                            >
                                {product.images && product.images.length > 0 && (
                                    <div className="card-image-preview">
                                        <img src={getImageUrl(product.images.find(img => img.is_primary)?.image_url || product.images[0].image_url)} alt={product.name} />
                                    </div>
                                )}
                                <div className="card-content">
                                    <h3>{product.name}</h3>
                                    <p>Rs. {parseFloat(product.price).toLocaleString()}</p>
                                    <span className="selection-indicator">
                                        {selectedProducts.includes(product.id) ? '✓ Selected' : 'Click to select'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="btn-primary" onClick={saveFeaturedProducts} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Featured Products'}
                    </button>
                </div>
            )}

            {activeTab === 'categories' && (
                <div className="tab-content">
                    <h2>Featured Categories Section</h2>
                    <p>Configure the section heading, then choose up to 4 categories with optional per-card text.</p>

                    {/* Section heading + subheading */}
                    <div className="ad-form" style={{ marginBottom: '24px' }}>
                        <div className="form-group">
                            <label>Section Heading</label>
                            <input
                                type="text"
                                value={sectionText.heading}
                                placeholder="e.g. Timeless Elegance"
                                onChange={(e) => setSectionText({ ...sectionText, heading: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Section Subheading</label>
                            <textarea
                                rows="2"
                                value={sectionText.subheading}
                                placeholder="e.g. Discover premium watches crafted for every moment."
                                onChange={(e) => setSectionText({ ...sectionText, subheading: e.target.value })}
                            />
                        </div>
                    </div>

                    <h3>Select Categories ({selectedCategories.length}/4)</h3>
                    <p style={{ fontSize: '13px', color: '#666' }}>
                        Edit each card's kicker, title, description, and button text in <strong>Categories → Edit</strong>.
                    </p>
                    <div className="selection-grid">
                        {allCategories.map((category) => {
                            const isSelected = selectedCategories.includes(category.id);
                            return (
                                <div
                                    key={category.id}
                                    className={`selection-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleCategoryToggle(category.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {category.image_url && (
                                        <div className="card-image-preview">
                                            <img src={getImageUrl(category.image_url)} alt={category.name} />
                                        </div>
                                    )}
                                    <div className="card-content">
                                        <h3>{category.name}</h3>
                                        <p>{category.description}</p>
                                        <span className="selection-indicator">
                                            {isSelected ? '✓ Selected' : 'Click to select'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <button className="btn-primary" onClick={saveFeaturedCategories} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Featured Categories'}
                    </button>
                </div>
            )}

            {activeTab === 'hero' && (
                <div className="tab-content">
                    <h2>Hero Products</h2>
                    <p>
                        Choose up to {MAX_HERO_PRODUCTS} products to stand in the home page hero.
                        Each one becomes a slide, shown after any campaign placed at the hero.
                    </p>

                    {heroSlides.length > 0 && (
                        <div className="ad-form" style={{ marginBottom: '24px' }}>
                            {heroSlides.map((slide, slideIndex) => {
                                const product = allProducts.find((p) => p.id === slide.product_id);
                                return (
                                    <div key={slide.product_id} className="hero-slide-fields">
                                        <div className="hero-slide-header">
                                            <h3>Slide {slideIndex + 1} — {product?.name || 'Selected product'}</h3>
                                            <button
                                                type="button"
                                                className="btn-danger"
                                                onClick={() => handleHeroToggle(slide.product_id)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <p className="hero-slide-hint">
                                            Leave a field blank to use the product's own wording.
                                        </p>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Brand line</label>
                                                <input
                                                    type="text"
                                                    value={slide.kicker}
                                                    placeholder={product?.category?.name || 'Category name'}
                                                    onChange={(e) => handleHeroCopyChange(slide.product_id, 'kicker', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Headline</label>
                                                <input
                                                    type="text"
                                                    value={slide.title}
                                                    placeholder={product?.name || 'Product name'}
                                                    onChange={(e) => handleHeroCopyChange(slide.product_id, 'title', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Tagline</label>
                                            <textarea
                                                rows="2"
                                                value={slide.description}
                                                placeholder="Opening line of the product description"
                                                onChange={(e) => handleHeroCopyChange(slide.product_id, 'description', e.target.value)}
                                            />
                                            <span className="hero-slide-hint">Press Enter for a second line.</span>
                                        </div>
                                        <div className="form-group">
                                            <label>Button text</label>
                                            <input
                                                type="text"
                                                value={slide.button_text}
                                                placeholder="Shop Now"
                                                onChange={(e) => handleHeroCopyChange(slide.product_id, 'button_text', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <h3>Select Products ({heroSlides.length}/{MAX_HERO_PRODUCTS})</h3>
                    <p style={{ fontSize: '13px', color: '#666' }}>
                        The hero shows the product's primary image on a light background, so a
                        cut-out shot works best.
                    </p>
                    <div className="selection-grid">
                        {allProducts.map((product) => {
                            const isSelected = heroSlides.some((slide) => slide.product_id === product.id);
                            return (
                                <div
                                    key={product.id}
                                    className={`selection-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleHeroToggle(product.id)}
                                >
                                    {product.images && product.images.length > 0 && (
                                        <div className="card-image-preview">
                                            <img src={getImageUrl(product.images.find(img => img.is_primary)?.image_url || product.images[0].image_url)} alt={product.name} />
                                        </div>
                                    )}
                                    <div className="card-content">
                                        <h3>{product.name}</h3>
                                        <p>Rs. {parseFloat(product.price).toLocaleString()}</p>
                                        <span className="selection-indicator">
                                            {isSelected ? '✓ Selected' : 'Click to select'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <button className="btn-primary" onClick={saveHeroProducts} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Hero Products'}
                    </button>
                </div>
            )}

        </div>
    );
};

export default DashboardManager;
